import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { api } from "@/lib/api"
import { PageLoader, ErrorState } from "@/components/PageStates"
import { Settings, ArrowUpRight, Users, DollarSign, Server, Mail, Activity, Clock, Zap, FileText, Shield, Package } from "lucide-react"
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"

const chartData = [
  { name: "May 1", revenue: 12000 },
  { name: "May 8", revenue: 15000 },
  { name: "May 15", revenue: 18000 },
  { name: "May 22", revenue: 22000 },
  { name: "May 29", revenue: 24820 },
]

const statusData = [
  { name: "API Server", status: "online", uptime: "99.9%", path: "/health" },
  { name: "Database", status: "online", uptime: "100%", path: "/health" },
  { name: "Worker", status: "online", uptime: "99.8%", path: "/health" },
  { name: "Telegram API", status: "online", uptime: "99.5%", path: "/bots" },
  { name: "Payment Gateway", status: "online", uptime: "100%", path: "/payments" },
  { name: "VPN Nodes", status: "warning", uptime: "98.2%", path: "/servers" },
  { name: "Backup Service", status: "online", uptime: "100%", path: "/backups" },
]

const activityData = [
  { title: "New user registered", desc: "user@example.com", time: "2m ago", color: "rgba(59,130,246,0.15)", iconColor: "#3B82F6", path: "/users" },
  { title: "New subscription", desc: "Plan: Premium — 1 Month", time: "10m ago", color: "rgba(139,92,246,0.15)", iconColor: "#8B5CF6", path: "/pricing" },
  { title: "Payment received", desc: "$9.99 from user@example.com", time: "15m ago", color: "rgba(34,197,94,0.15)", iconColor: "#22C55E", path: "/payments" },
  { title: "Server node updated", desc: "Node: IR-T3-02", time: "30m ago", color: "rgba(249,115,22,0.15)", iconColor: "#F97316", path: "/servers" },
  { title: "High memory usage", desc: "Worker 2", time: "45m ago", color: "rgba(239,68,68,0.15)", iconColor: "#EF4444", path: "/health" },
]

const plansData = [
  { name: "Premium — 1 Month", users: "3,482", revenue: "$12,450", growth: "+15.2%" },
  { name: "Premium — 3 Month", users: "2,150", revenue: "$7,890", growth: "+11.3%" },
  { name: "Premium — 6 Month", users: "1,820", revenue: "$6,240", growth: "+8.7%" },
  { name: "Premium — 1 Year", users: "1,283", revenue: "$5,120", growth: "+21.4%" },
]

const actions = [
  { label: "Create User", color: "#3B82F6", icon: Users, path: "/users" },
  { label: "Add Server", color: "#22C55E", icon: Server, path: "/servers" },
  { label: "Create Plan", color: "#8B5CF6", icon: Package, path: "/pricing" },
  { label: "Send Broadcast", color: "#F59E0B", icon: Zap, path: "/messages" },
  { label: "Generate Config", color: "#EF4444", icon: FileText, path: "/configs" },
  { label: "View Logs", color: "#3B82F6", icon: Activity, path: "/logs" },
]

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: '#1E293B', border: '1px solid #334155', borderRadius: 8, padding: '8px 12px' }}>
      <div style={{ fontSize: 12, color: '#64748B' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#F8FAFC' }}>${payload[0].value.toLocaleString()}</div>
    </div>
  )
}

export default function DashboardPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/dashboard"),
    refetchInterval: 30000,
  })

  if (isLoading) return <PageLoader />
  if (error) return <ErrorState onRetry={refetch} />

  const stats = data?.data || {}

  return (
    <div className="animate-fade" style={{ padding: 32, maxWidth: '100%' }}>
      {/* Title */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 36, fontWeight: 700, color: '#F8FAFC', marginBottom: 4 }}>Dashboard</h1>
          <p style={{ fontSize: 14, color: '#94A3B8' }}>Welcome back, <span style={{ color: '#3B82F6' }}>Kourosh Admin</span>! Here's what's happening with your platform.</p>
        </div>
        <button style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: '#3B82F6', color: '#fff', borderRadius: 10, fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer', transition: 'all 180ms' }}>
          <Settings size={14} /> Customize
        </button>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, marginBottom: 24 }}>
        {[
          { title: "Total Users", value: (stats.users?.total || 0).toLocaleString(), change: "+12.5%", icon: Users, bg: "#3B82F6", path: "/users" },
          { title: "Total Revenue", value: `$${(stats.revenue?.total || 0).toLocaleString()}`, change: "+18.7%", icon: DollarSign, bg: "#22C55E", path: "/payments" },
          { title: "Active Servers", value: String(stats.panels?.total || 0), change: "+3.2%", icon: Server, bg: "#8B5CF6", path: "/servers" },
          { title: "Active Subscriptions", value: (stats.users?.total || 0).toLocaleString(), change: "+8.9%", icon: Mail, bg: "#F97316", path: "/subscriptions" },
        ].map((kpi) => (
          <Link key={kpi.title} to={kpi.path} style={{ background: '#111827', border: '1px solid #1F2937', borderRadius: 18, padding: 24, height: 140, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', textDecoration: 'none', transition: 'all 180ms', cursor: 'pointer' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#94A3B8', marginBottom: 8 }}>{kpi.title}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#F8FAFC', marginBottom: 12 }}>{kpi.value}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ArrowUpRight size={14} color="#22C55E" />
                <span style={{ fontSize: 12, fontWeight: 500, color: '#22C55E' }}>{kpi.change}</span>
                <span style={{ fontSize: 12, color: '#64748B' }}>from last month</span>
              </div>
            </div>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <kpi.icon size={20} color="#fff" />
            </div>
          </Link>
        ))}
      </div>

      {/* Second Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 24 }}>
        {/* Revenue Chart */}
        <div style={{ background: '#111827', border: '1px solid #1F2937', borderRadius: 18, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#F8FAFC' }}>Revenue Overview</h3>
            <button style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#1E293B', border: '1px solid #334155', borderRadius: 8, fontSize: 13, color: '#94A3B8', cursor: 'pointer' }}>
              This Month <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
            </button>
          </div>
          <div style={{ height: 200, marginBottom: 24 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="#1E293B" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v/1000}K`} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={2} fill="url(#gradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, borderTop: '1px solid #1F2937', paddingTop: 16 }}>
            {[
              { label: "Total Revenue", value: "$24,820", color: "#3B82F6" },
              { label: "Net Revenue", value: "$21,350", color: "#3B82F6" },
              { label: "Orders", value: "1,248", color: "#F8FAFC" },
              { label: "Refunds", value: "$3,470", color: "#EF4444" },
            ].map((s) => (
              <div key={s.label}>
                <div style={{ fontSize: 12, color: '#64748B' }}>{s.label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: s.color, marginTop: 4 }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* System Status */}
        <div style={{ background: '#111827', border: '1px solid #1F2937', borderRadius: 18, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#F8FAFC' }}>System Status</h3>
            <Link to="/health" style={{ fontSize: 13, fontWeight: 500, color: '#3B82F6', textDecoration: 'none' }}>View All</Link>
          </div>
          {statusData.map((s, i) => (
            <Link key={i} to={s.path} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 8px', borderBottom: i < statusData.length - 1 ? '1px solid #1F2937' : 'none', textDecoration: 'none', transition: 'background 180ms', borderRadius: 8, margin: '0 -8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.status === 'online' ? '#22C55E' : '#F59E0B' }} />
                <span style={{ fontSize: 13, color: '#F8FAFC' }}>{s.name}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: s.status === 'online' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', color: s.status === 'online' ? '#22C55E' : '#F59E0B' }}>{s.status === 'online' ? 'Online' : 'Warning'}</span>
                <span style={{ fontSize: 12, color: '#94A3B8', width: 45, textAlign: 'right' }}>{s.uptime}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Third Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24, marginBottom: 24 }}>
        {/* Recent Activity */}
        <div style={{ background: '#111827', border: '1px solid #1F2937', borderRadius: 18, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#F8FAFC' }}>Recent Activity</h3>
            <Link to="/events" style={{ fontSize: 13, fontWeight: 500, color: '#3B82F6', textDecoration: 'none' }}>View All</Link>
          </div>
          {activityData.map((a, i) => (
            <Link key={i} to={a.path} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: i < activityData.length - 1 ? '1px solid #1F2937' : 'none', textDecoration: 'none' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Users size={16} color={a.iconColor} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#F8FAFC' }}>{a.title}</div>
                <div style={{ fontSize: 12, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.desc}</div>
              </div>
              <div style={{ fontSize: 11, color: '#64748B', flexShrink: 0 }}>{a.time}</div>
            </Link>
          ))}
        </div>

        {/* Top Plans */}
        <div style={{ background: '#111827', border: '1px solid #1F2937', borderRadius: 18, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#F8FAFC' }}>Top Plans</h3>
            <Link to="/pricing" style={{ fontSize: 13, fontWeight: 500, color: '#3B82F6', textDecoration: 'none' }}>View All</Link>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1F2937' }}>
                {["Plan", "Users", "Revenue", "Growth"].map(h => (
                  <th key={h} style={{ paddingBottom: 12, textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {plansData.map((p, i) => (
                <tr key={i} style={{ borderBottom: i < plansData.length - 1 ? '1px solid #1F2937' : 'none' }}>
                  <td style={{ padding: '12px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="14" height="14" fill="#F59E0B" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      </div>
                      <span style={{ fontSize: 13, color: '#F8FAFC' }}>{p.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px 0', fontSize: 13, color: '#F8FAFC' }}>{p.users}</td>
                  <td style={{ padding: '12px 0', fontSize: 13, color: '#F8FAFC' }}>{p.revenue}</td>
                  <td style={{ padding: '12px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <ArrowUpRight size={12} color="#22C55E" />
                      <span style={{ fontSize: 12, fontWeight: 500, color: '#22C55E' }}>{p.growth}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Server Locations */}
        <div style={{ background: '#111827', border: '1px solid #1F2937', borderRadius: 18, padding: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#F8FAFC', marginBottom: 16 }}>Server Location</h3>
          <div style={{ height: 160, borderRadius: 10, overflow: 'hidden', marginBottom: 16, background: '#1E293B', position: 'relative' }}>
            <svg viewBox="0 0 800 400" style={{ width: '100%', height: '100%', opacity: 0.4 }}>
              <path d="M150,120 Q200,80 250,120 T350,110 T450,130 T550,100 T650,120" fill="none" stroke="#334155" strokeWidth="1.5" />
              <path d="M100,200 Q200,160 300,200 T500,190 T700,200" fill="none" stroke="#334155" strokeWidth="1.5" />
              <circle cx="200" cy="150" r="4" fill="#3B82F6" className="animate-pulse" />
              <circle cx="500" cy="130" r="4" fill="#3B82F6" className="animate-pulse" />
              <circle cx="350" cy="180" r="4" fill="#22C55E" className="animate-pulse" />
              <circle cx="650" cy="160" r="4" fill="#F59E0B" className="animate-pulse" />
            </svg>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {[
              { value: stats.panels?.total || 0, label: "Total Servers", color: '#F8FAFC' },
              { value: "112", label: "Online", color: '#22C55E' },
              { value: "8", label: "Warning", color: '#F59E0B' },
              { value: "8", label: "Offline", color: '#EF4444' },
            ].map((s) => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: '#64748B' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginBottom: 24 }}>
        <div style={{ gridColumn: '2 / 4' }}>
          <div style={{ background: '#111827', border: '1px solid #1F2937', borderRadius: 18, padding: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#F8FAFC', marginBottom: 16 }}>Quick Actions</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {actions.map((a) => (
                <Link key={a.label} to={a.path} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 16, borderRadius: 12, border: '1px solid #334155', background: '#1E293B', textDecoration: 'none', transition: 'all 180ms', cursor: 'pointer' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${a.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <a.icon size={20} color={a.color} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#94A3B8' }}>{a.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 24, borderTop: '1px solid #1F2937' }}>
        <span style={{ fontSize: 12, color: '#64748B' }}>© 2025 KouroshShield. All rights reserved.</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 12, color: '#64748B' }}>Version 1.0.0-rc6</span>
          <a href="#" style={{ fontSize: 12, color: '#3B82F6', textDecoration: 'none' }}>Documentation</a>
        </div>
      </div>
    </div>
  )
}
